import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { withFirebase } from '../Firebase';
import * as ROUTES from '../../constants/routes';

const ResetPasswordActionPageBase = ({ firebase }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [passwordOne, setPasswordOne] = useState('');
  const [passwordTwo, setPasswordTwo] = useState('');
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const handledCodeRef = useRef('');

  const oobCode = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('oobCode') || '';
  }, [location.search]);

  useEffect(() => {
    let active = true;

    if (!oobCode) {
      setStatus('error');
      setError(t('resetPasswordAction.invalid_link'));
      return () => {
        active = false;
      };
    }

    if (handledCodeRef.current === oobCode) {
      return () => {
        active = false;
      };
    }

    handledCodeRef.current = oobCode;

    firebase.doVerifyPasswordResetCode(oobCode)
      .then(accountEmail => {
        if (!active) return;
        setEmail(accountEmail || '');
        setStatus('ready');
        setError('');
      })
      .catch(actionError => {
        if (!active) return;
        setStatus('error');
        setError(actionError?.message || t('resetPasswordAction.invalid_link'));
      });

    return () => {
      active = false;
    };
  }, [firebase, oobCode, t]);

  const handleSubmit = async event => {
    event.preventDefault();

    if (passwordOne !== passwordTwo) {
      setError(t('resetPasswordAction.password_mismatch'));
      return;
    }

    setStatus('submitting');
    setError('');

    try {
      await firebase.doConfirmPasswordReset(oobCode, passwordOne);
      setStatus('success');
      setTimeout(() => navigate(ROUTES.SIGN_IN), 1200);
    } catch (actionError) {
      setStatus('ready');
      setError(actionError?.message || t('resetPasswordAction.error_fallback'));
    }
  };

  const isInvalid = !passwordOne || passwordOne !== passwordTwo;

  return (
    <main className='auth-main'>
      <div className='auth-left'>
        <div className='auth-card'>
          <h1>{t('resetPasswordAction.title')}</h1>
          <div className='auth-form'>
            {status === 'loading' ? (
              <p>{t('resetPasswordAction.processing')}</p>
            ) : null}
            {status === 'error' ? (
              <>
                <p className='auth-error'>{error}</p>
                <Link to={ROUTES.PASSWORD_FORGET}>{t('resetPasswordAction.request_new_link')}</Link>
              </>
            ) : null}
            {status === 'success' ? (
              <p>{t('resetPasswordAction.success_message')}</p>
            ) : null}
            {status === 'ready' || status === 'submitting' ? (
              <>
                {email ? (
                  <p>{t('resetPasswordAction.email_hint', { email })}</p>
                ) : null}
                {error ? (
                  <p className='auth-error'>{error}</p>
                ) : null}
                <form onSubmit={handleSubmit} className='auth-form'>
                  <input
                    type='password'
                    value={passwordOne}
                    onChange={event => setPasswordOne(event.target.value)}
                    placeholder={t('passwordChange.new_password_placeholder')}
                    aria-label={t('passwordChange.new_password_placeholder')}
                    autoComplete='new-password'
                    required
                  />
                  <input
                    type='password'
                    value={passwordTwo}
                    onChange={event => setPasswordTwo(event.target.value)}
                    placeholder={t('passwordChange.confirm_password_placeholder')}
                    aria-label={t('passwordChange.confirm_password_placeholder')}
                    autoComplete='new-password'
                    required
                  />
                  <button type='submit' disabled={status === 'submitting' || isInvalid}>
                    {t('resetPasswordAction.submit_button')}
                  </button>
                </form>
              </>
            ) : null}
          </div>
        </div>
      </div>
      <div className='auth-right'>
        <img
          src='https://picsum.photos/1200/1200'
          alt={t('resetPasswordAction.image_alt')}
          loading='lazy'
          sizes='(max-width: 600px) 200px, (max-width: 1200px) 400px, 800px'
        />
      </div>
    </main>
  );
};

export default withFirebase(ResetPasswordActionPageBase);